import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, retry, skip, take } from 'rxjs/operators';

import {
  DynamicTicketType,
  DynamicTicketTypePmsSettings,
  DynamicTicketTypeService,
} from '@app/services/dynamic-ticket-type.service';
import { SystemConfigService } from '@app/services/system-config.service';
import { TicketsService } from '@app/services/tickets.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { ImageCaptureMode } from '@main-application/inspections/models/rest-inspections-model.interface';
import { BuiltInTicketType } from '@main-application/management/pages/service-configuration/dynamic-tickets/components/add-ticket-type-dialog/add-ticket-type-dialog.component';
import { selectTenantData } from '@main-application/store/selectors/user.selectors';
import { FadeTextComponent } from '@shared/components/ellipsis-one-row/fade-text.component';
import { ButtonType } from '@shared/enums/button-type';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { PmsCredentialModel } from '@shared/interfaces/pms-credential-model.interface';
import { IRadioButtonOption, makeRadioButtonOptions } from '@shared/interfaces/radio-button-option.interface';
import { RestTicketCategoryModel } from '@shared/interfaces/turnover.interface';
import { ButtonModule } from '@ui-components/components/button/button.module';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { NumberInputModule } from '@ui-components/controls/number-input/number-input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

import { CostFileTicketPartsSetupOptionComponent } from './cost-file-ticket-parts-setup-option/cost-file-ticket-parts-setup-option.component';
import { CostFileTicketPartsSetupPlaceholderComponent } from './cost-file-ticket-parts-setup-placeholder/cost-file-ticket-parts-setup-placeholder.component';
import {
  createTemplateServiceTaskOption,
  deleteTemplateServiceTaskOption,
  loadTemplateServiceTaskOptions,
  updateTemplateServiceTaskOption,
} from '../../../../store/actions/inspection-template-service-task-options.actions';
import {
  selectTemplateServiceTaskOptions,
  selectTemplateServiceTaskOptionsError,
  selectTemplateServiceTaskOptionsLoading,
} from '../../../../store/selectors/inspection-template-service-task-options.selectors';
import {
  DynamicTicketTypeSettingsOverride,
  FileAsTypeServiceTaskOptionsModel,
  InspectionTemplateServiceTaskEstimatedCostInputMethods,
  InspectionTemplateServiceTaskOptionTypes,
  RestInspectionTemplateServiceTaskOptionsModel,
  RestInspectionTemplateServiceTaskOptionsModelBase,
  SubmitAsOptions,
} from '../../interfaces/inspection-template-service-task-options.interface';

export interface CostFileTicketPartsSetupModalData {
  templateId: number;
}

interface FieldOptionConfig {
  type: InspectionTemplateServiceTaskOptionTypes;
  label: string;
  previewValue: string;
  icon: EIcon;
}

interface TopicOption {
  value: number;
  label: string;
}

interface CategoryOption {
  value: number;
  label: string;
  topics: TopicOption[];
}

interface FileAsTypeOverrideEntry {
  key: string;
  dynamicTicketTypeId: number;
  apiCredentialsId: number | null;
  isPlaceholder?: boolean;
}

interface FileAsTypeOverrideFormValue {
  defaultOrigin: string | null;
  allowedOrigins: string[];
  defaultCategoryId: string | null;
  defaultTopicId: string | null;
  allowedCategories: string[];
}

interface SelectAllSnapshot {
  categories: string[];
}

@UntilDestroy()
@Component({
  selector: 'app-cost-file-ticket-parts-setup-modal',
  templateUrl: './cost-file-ticket-parts-setup-modal.component.html',
  styleUrls: ['./cost-file-ticket-parts-setup-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDividerModule,
    MatTooltipModule,
    ReactiveFormsModule,
    ButtonModule,
    RadioButtonDropdownModule,
    NumberInputModule,
    InputModule,
    InputAreaModule,
    IconComponent,
    CostFileTicketPartsSetupOptionComponent,
    CostFileTicketPartsSetupPlaceholderComponent,
    FadeTextComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostFileTicketPartsSetupModalComponent extends PuSubscribable implements OnInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  readonly DialogResult = DialogResult;

  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  public dialogRef: MatDialogRef<CostFileTicketPartsSetupModalComponent> = inject(MatDialogRef);
  public data: CostFileTicketPartsSetupModalData = inject(MAT_DIALOG_DATA);
  private store: Store<{}> = inject(Store);
  private fb: FormBuilder = inject(FormBuilder);
  private dynamicTicketTypeService: DynamicTicketTypeService = inject(DynamicTicketTypeService);
  private ticketsService: TicketsService = inject(TicketsService);
  private systemConfigService: SystemConfigService = inject(SystemConfigService);

  options: RestInspectionTemplateServiceTaskOptionsModel[] = [];
  loading = toSignal(this.store.select(selectTemplateServiceTaskOptionsLoading), { initialValue: false });
  initialLoadComplete = false;
  private hasSeenInitialLoading = false;
  private hasReceivedOptions = false;
  private pmsNames: string | null = null;
  isAddFieldMenuOpen = false;
  isAddWorkflowMenuOpen = false;
  showMobilePreview = false;
  useFileIcon = false;
  currentTime = new Date();
  readonly ImageCaptureMode = ImageCaptureMode;

  estimatedCostForm: FormGroup;
  fileAsTypeForm: FormGroup;
  responsiblePartyForm: FormGroup;
  dueDateForm: FormGroup;
  assigneeForm: FormGroup;
  sendToBoardForm: FormGroup;

  readonly InspectionTemplateServiceTaskOptionTypes = InspectionTemplateServiceTaskOptionTypes;
  readonly SubmitAsOptions = SubmitAsOptions;

  readonly EstimatedCost = InspectionTemplateServiceTaskOptionTypes.EstimatedCost;
  readonly FileAsType = InspectionTemplateServiceTaskOptionTypes.FileAsType;
  readonly ResponsibleParty = InspectionTemplateServiceTaskOptionTypes.ResponsibleParty;
  readonly DueDate = InspectionTemplateServiceTaskOptionTypes.DueDate;
  readonly Assignee = InspectionTemplateServiceTaskOptionTypes.Assignee;
  readonly SendToBoard = InspectionTemplateServiceTaskOptionTypes.SendToBoard;
  readonly ButtonType = ButtonType;
  readonly workflowOptionsEnabled = true;
  readonly allCategoriesTreeValue = '-1';
  readonly categorySummaryLimit = 4;

  submitAsMenuOpen = false;
  workOrderTypeMenuOpen = false;
  ticketTypeMenuOpen = false;

  dynamicTicketTypes: DynamicTicketType[] = [];
  workOrderTypeOptions: IRadioButtonOption<number>[] = [];
  ticketTypeOptions: IRadioButtonOption<number>[] = [];
  private dynamicTicketTypesMap = new Map<number, DynamicTicketType>();

  private apiCredentialsMap = new Map<number, { type: string; residentTicketOrigins: string[] }>();
  private pmsSetupLoading = new Set<number>();

  categories: CategoryOption[] = [];
  topics: TopicOption[] = [];
  categorySelectOptions: IRadioButtonOption<string>[] = [];
  private topicsByCategory = new Map<number, IRadioButtonOption<string>[]>();
  private categoryLabelMap = new Map<string, string>();
  private topicLabelMap = new Map<string, string>();

  fileAsTypeOverrideEntries: FileAsTypeOverrideEntry[] = [];
  private fileAsTypeOverrideForms = new Map<string, FormGroup>();
  private fileAsTypeOverridesByKey = new Map<string, DynamicTicketTypeSettingsOverride>();
  private overrideOriginOptions = new Map<string, IRadioButtonOption<string>[]>();
  private overrideSelectAllState = new Map<string, SelectAllSnapshot>();
  private dynamicTicketTypeSettingsByType = new Map<number, DynamicTicketTypePmsSettings[]>();
  private dynamicTicketTypeSettingsLoading = new Set<number>();

  readonly fieldOptions: FieldOptionConfig[] = [
    {
      type: InspectionTemplateServiceTaskOptionTypes.Assignee,
      label: 'Assignee',
      previewValue: 'Select',
      icon: EIcon.ASSIGNEE,
    },
    {
      type: InspectionTemplateServiceTaskOptionTypes.DueDate,
      label: 'Due date',
      previewValue: 'Select',
      icon: EIcon.VENDOR_CALENDAR,
    },
    {
      type: InspectionTemplateServiceTaskOptionTypes.EstimatedCost,
      label: 'Estimated cost',
      previewValue: '$',
      icon: EIcon.DOLLAR_MODERN,
    },
    {
      type: InspectionTemplateServiceTaskOptionTypes.ResponsibleParty,
      label: 'Responsibility',
      previewValue: 'Select',
      icon: EIcon.RESPONSIBLE_PARTY,
    },
    {
      type: InspectionTemplateServiceTaskOptionTypes.SendToBoard,
      label: 'Send to board',
      previewValue: 'Select',
      icon: EIcon.SEND_TO_BOARD,
    },
    {
      type: InspectionTemplateServiceTaskOptionTypes.FileAsType,
      label: 'Submit as',
      previewValue: 'Select',
      icon: EIcon.VENDOR_CHECK,
    },
  ];

  autoGeneratedTicketTitlePlaceholder =
    '<Dynamic title: auto-generated from the names of the inspection, area, item, and rating>';
  autoGeneratedTicketDescriptionPlaceholder =
    '<Dynamic description: auto-generated from inspection, area, item, rating, and item question answers>';

  readonly fieldDefinitionOptions = ['User input', 'Workflow'];
  selectedFieldDefinition = this.fieldDefinitionOptions[0];

  get hasFieldDefinitionOptions(): boolean {
    return this.fieldDefinitionOptions.length > 1;
  }

  setFieldDefinition(option: string): void {
    this.selectedFieldDefinition = option;
    const selectedOptionType = this.selectedMobileFieldType ?? this.selectedWorkflowFieldKey;
    if (selectedOptionType === null) {
      return;
    }

    const userInput = option === 'User input';
    this.updateOptionUserInput(selectedOptionType, userInput);

    if (
      selectedOptionType === InspectionTemplateServiceTaskOptionTypes.FileAsType &&
      option === 'Workflow' &&
      this.submitAsValue === SubmitAsOptions.AnyServiceTask
    ) {
      this.setSubmitAsAvailability(false);
    }
  }

  get selectedOptionType(): InspectionTemplateServiceTaskOptionTypes | null {
    return this.selectedMobileFieldType ?? this.selectedWorkflowFieldKey;
  }

  get isInitialLoading(): boolean {
    return !this.initialLoadComplete && this.loading();
  }

  get isFileAsTypeSelected(): boolean {
    return this.selectedOptionType === InspectionTemplateServiceTaskOptionTypes.FileAsType;
  }

  get isSubmitAsLoading(): boolean {
    if (!this.isFileAsTypeSelected) return false;
    if (this.fileAsTypeReferenceLoading) return true;

    const selectedWorkOrderIds = this.getSelectedDynamicTicketTypeIdsByCategory('workOrder');
    return selectedWorkOrderIds.some(id => this.dynamicTicketTypeSettingsLoading.has(id));
  }

  get isContentLoading(): boolean {
    return this.isInitialLoading || this.isSubmitAsLoading;
  }

  get submitAsValue(): SubmitAsOptions {
    return (this.fileAsTypeForm?.get('submitAs')?.value as SubmitAsOptions) ?? SubmitAsOptions.PropUpTicket;
  }

  getMenuTriggerWords(label: string | null | undefined): string[] {
    return (label || '').split(' ').filter(word => word.length);
  }

  get isOnlyOneWorkOrder(): boolean {
    return !this.fileAsTypeForm?.get('canSubmitMoreThanOneTicket')?.value;
  }

  get showWorkOrderTypeSection(): boolean {
    return this.submitAsValue === SubmitAsOptions.WorkOrder || this.submitAsValue === SubmitAsOptions.AnyServiceTask;
  }

  get showTicketTypeSection(): boolean {
    return this.submitAsValue === SubmitAsOptions.PropUpTicket || this.submitAsValue === SubmitAsOptions.AnyServiceTask;
  }

  selectedFieldTypes: InspectionTemplateServiceTaskOptionTypes[] = [];
  workflowFieldTypes: InspectionTemplateServiceTaskOptionTypes[] = [];
  selectedMobileFieldType: InspectionTemplateServiceTaskOptionTypes | null = null;
  selectedWorkflowFieldKey: InspectionTemplateServiceTaskOptionTypes | null = null;

  private estimatedCostOptionId: number | null = null;
  private fileAsTypeOptionId: number | null = null;
  private responsiblePartyOptionId: number | null = null;
  private dueDateOptionId: number | null = null;
  private assigneeOptionId: number | null = null;
  private sendToBoardOptionId: number | null = null;
  private pendingAddOptionType: InspectionTemplateServiceTaskOptionTypes | null = null;
  private pendingAddUserInput: boolean | null = null;
  private fileAsTypeFastSave$ = new Subject<void>();
  private fileAsTypeSlowSave$ = new Subject<void>();
  private fileAsTypeReferenceLoading = false;
  private fileAsTypeReferenceLoaded = false;
  private fileAsTypeReferenceFailed = false;
  private fileAsTypeReferencePending = 0;

  get hasEstimatedCost(): boolean {
    return this.estimatedCostOptionId !== null;
  }

  get hasFileAsType(): boolean {
    return this.fileAsTypeOptionId !== null;
  }

  get hasResponsibleParty(): boolean {
    return this.responsiblePartyOptionId !== null;
  }

  get hasDueDate(): boolean {
    return this.dueDateOptionId !== null;
  }

  get hasAssignee(): boolean {
    return this.assigneeOptionId !== null;
  }

  get hasSendToBoard(): boolean {
    return this.sendToBoardOptionId !== null;
  }

  get selectedFieldOptions(): FieldOptionConfig[] {
    return this.fieldOptions.filter(option => this.selectedFieldTypes.includes(option.type));
  }

  get mobileFieldTypes(): InspectionTemplateServiceTaskOptionTypes[] {
    return this.selectedFieldTypes;
  }

  get workflowFieldOptions(): FieldOptionConfig[] {
    return this.fieldOptions.filter(option => this.workflowFieldTypes.includes(option.type));
  }

  readonly workflowPreviewHeight = 456;

  get configPanelHeight(): number {
    const baseHeight = 598;
    const increase = this.workflowFieldOptions.length * 56;
    return baseHeight + increase;
  }

  defaultValueOptions: IRadioButtonOption<string>[] = [
    { value: 'Total', label: 'Total' },
    { value: 'T&M', label: 'T&M' },
  ];

  userEnterCostOptions: IRadioButtonOption<boolean>[] = [
    { value: true, label: 'Manually' },
    { value: false, label: 'Set valid range' },
  ];

  fieldRequirementOptions: IRadioButtonOption<boolean>[] = [
    { value: true, label: 'Required' },
    { value: false, label: 'Optional' },
  ];

  getFieldRequirementValue(value: boolean): IRadioButtonOption<boolean> | null {
    return this.fieldRequirementOptions.find(opt => opt.value === value) || null;
  }

  private initializeForms(): void {
    this.estimatedCostForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.EstimatedCost],
      defaultValue: [null],
      fieldRequired: [false],
      userEnterManually: [false],
      setValidRange: [false],
      throughPartsCatalog: [false],
      querySparePartsFrom: [null],
      defaultValueInput: [null],
    });

    this.fileAsTypeForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.FileAsType],
      submitAs: [SubmitAsOptions.PropUpTicket],
      defaultDynamicTicketType: [null],
      availableToUserDynamicTicketTypes: [[]],
      fieldRequired: [false],
      canSubmitMoreThanOneTicket: [false],
      autoGeneratedTicketTitle: [true],
      autoGeneratedTicketDescription: [true],
      customTicketTitle: [''],
      customTicketDescription: [''],
      userCanEditTicketTitle: [true],
      userCanEditTicketDescription: [true],
    });

    this.responsiblePartyForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.ResponsibleParty],
      responsibleParty: [null],
    });

    this.dueDateForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.DueDate],
      dueDateDays: [null],
      dueDateDescription: [null],
    });

    this.assigneeForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.Assignee],
      assignee: [null],
    });

    this.sendToBoardForm = this.fb.group({
      optionType: [InspectionTemplateServiceTaskOptionTypes.SendToBoard],
      boardId: [null],
      boardColumnId: [null],
    });
  }

  ngOnInit(): void {
    this.initializeForms();
    this.registerFileAsTypeFormListeners();

    this.store.dispatch(loadTemplateServiceTaskOptions({ templateId: this.data.templateId }));

    this.store
      .select(selectTenantData)
      .pipe(
        filter(tenantData => !!tenantData),
        take(1)
      )
      .subscribe(tenantData => {
        this.pmsNames = tenantData?.pmses && tenantData.pmses.trim() !== '' ? tenantData.pmses : null;
        this.syncFileAsTypeOverrides();
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);

    this.store
      .select(selectTemplateServiceTaskOptionsLoading)
      .pipe(distinctUntilChanged())
      .subscribe(isLoading => {
        this.updateInitialLoadingState(isLoading);
      })
      .untilDestroyed(this);

    this.store
      .select(selectTemplateServiceTaskOptions)
      .pipe(untilDestroyed(this))
      .subscribe(options => {
        const templateOptions = (options || []).filter(option => option.inspectionTemplateId === this.data.templateId);
        this.options = templateOptions;
        this.hasReceivedOptions = true;
        this.updateInitialLoadingState(this.loading());
        this.populateForms();
        this.setSelectedFieldTypesFromOptions();
        if (this.pendingAddOptionType !== null) {
          const wasAdded = this.options.some(option => option.optionType === this.pendingAddOptionType);
          if (wasAdded) {
            if (this.pendingAddUserInput === false) {
              this.selectedWorkflowFieldKey = this.pendingAddOptionType;
              this.selectedMobileFieldType = null;
              this.selectedFieldDefinition = 'Workflow';
            } else {
              this.selectedMobileFieldType = this.pendingAddOptionType;
              this.selectedWorkflowFieldKey = null;
              this.selectedFieldDefinition = 'User input';
            }
            this.pendingAddOptionType = null;
            this.pendingAddUserInput = null;
          }
        }
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);

    this.store
      .select(selectTemplateServiceTaskOptionsError)
      .pipe(untilDestroyed(this))
      .subscribe(error => {
        if (error && this.pendingAddOptionType !== null) {
          this.pendingAddOptionType = null;
          this.pendingAddUserInput = null;
          this.cdr.markForCheck();
        }
      })
      .untilDestroyed(this);
  }

  private registerFileAsTypeFormListeners(): void {
    this.fileAsTypeFastSave$
      .pipe(debounceTime(250))
      .subscribe(() => {
        this.saveFileAsTypeSettings();
      })
      .untilDestroyed(this);

    this.fileAsTypeSlowSave$
      .pipe(debounceTime(3000))
      .subscribe(() => {
        this.saveFileAsTypeSettings();
      })
      .untilDestroyed(this);

    this.fileAsTypeForm
      .get('customTicketTitle')
      ?.valueChanges.pipe(skip(1))
      .subscribe(() => {
        this.fileAsTypeSlowSave$.next();
      })
      .untilDestroyed(this);

    this.fileAsTypeForm
      .get('customTicketDescription')
      ?.valueChanges.pipe(skip(1))
      .subscribe(() => {
        this.fileAsTypeSlowSave$.next();
      })
      .untilDestroyed(this);

    this.fileAsTypeForm
      .get('submitAs')
      ?.valueChanges.pipe(skip(1))
      .subscribe(value => {
        this.handleSubmitAsChange(value as SubmitAsOptions);
      })
      .untilDestroyed(this);

    this.fileAsTypeForm.valueChanges
      .pipe(skip(1))
      .subscribe(() => {
        this.syncFileAsTypeOverrides();
        this.fileAsTypeFastSave$.next();
      })
      .untilDestroyed(this);
  }

  private updateInitialLoadingState(isLoading: boolean): void {
    if (this.initialLoadComplete) {
      return;
    }
    if (isLoading) {
      this.hasSeenInitialLoading = true;
      return;
    }
    if (this.hasSeenInitialLoading || this.hasReceivedOptions) {
      this.initialLoadComplete = true;
      this.cdr.markForCheck();
    }
  }

  private hasPmsConnection(): boolean {
    return !!this.pmsNames;
  }

  private loadFileAsTypeReferenceData(): void {
    if (this.fileAsTypeReferenceLoading || this.fileAsTypeReferenceLoaded) {
      return;
    }
    this.fileAsTypeReferenceLoading = true;
    this.fileAsTypeReferenceFailed = false;
    this.fileAsTypeReferencePending = 3;
    this.cdr.markForCheck();
    const finalizeLoad = (): void => {
      this.fileAsTypeReferencePending -= 1;
      if (this.fileAsTypeReferencePending > 0) {
        return;
      }
      this.fileAsTypeReferenceLoading = false;
      if (!this.fileAsTypeReferenceFailed) {
        this.fileAsTypeReferenceLoaded = true;
      }
      this.cdr.markForCheck();
    };

    this.dynamicTicketTypeService
      .getCachedDynamicTicketTypes()
      .pipe(retry(1), take(1))
      .subscribe({
        next: types => {
          this.setDynamicTicketTypes(types || []);
          this.syncFileAsTypeOverrides();
          this.cdr.markForCheck();
          finalizeLoad();
        },
        error: () => {
          this.fileAsTypeReferenceFailed = true;
          finalizeLoad();
        },
      })
      .untilDestroyed(this);

    this.ticketsService
      .getCategories()
      .pipe(retry(1), take(1))
      .subscribe({
        next: categories => {
          this.setCategoryOptions(categories || []);
          this.cdr.markForCheck();
          finalizeLoad();
        },
        error: () => {
          this.fileAsTypeReferenceFailed = true;
          finalizeLoad();
        },
      })
      .untilDestroyed(this);

    this.systemConfigService
      .getSystemConfig()
      .pipe(retry(1), take(1))
      .subscribe({
        next: credentials => {
          this.captureCredentialOrigins(credentials || []);
          this.overrideOriginOptions.clear();
          this.syncFileAsTypeOverrides();
          this.cdr.markForCheck();
          finalizeLoad();
        },
        error: () => {
          this.fileAsTypeReferenceFailed = true;
          finalizeLoad();
        },
      })
      .untilDestroyed(this);
  }

  private ensureFileAsTypeReferenceDataLoaded(): void {
    if (this.fileAsTypeReferenceLoading || this.fileAsTypeReferenceLoaded) {
      return;
    }
    this.loadFileAsTypeReferenceData();
  }

  private populateForms(): void {
    this.estimatedCostOptionId = null;
    this.fileAsTypeOptionId = null;
    this.responsiblePartyOptionId = null;
    this.dueDateOptionId = null;
    this.assigneeOptionId = null;
    this.sendToBoardOptionId = null;

    const estimatedCost = this.options.find(
      o => o.optionType === InspectionTemplateServiceTaskOptionTypes.EstimatedCost
    );
    if (estimatedCost && estimatedCost.settingsJson) {
      try {
        const parsed = JSON.parse(estimatedCost.settingsJson);
        this.estimatedCostForm.patchValue({
          defaultValue: parsed.defaultValue || null,
          fieldRequired: parsed.fieldRequired ?? parsed.isRequired ?? false,
          userEnterManually: parsed.userEnterManually || false,
          setValidRange: parsed.setValidRange || false,
          throughPartsCatalog: parsed.throughPartsCatalog || false,
          querySparePartsFrom: parsed.querySparePartsFrom || null,
          defaultValueInput: parsed.defaultValueInput || null,
        });
      } catch {}
      this.estimatedCostOptionId = estimatedCost.id;
    }

    const fileAsType = this.options.find(o => o.optionType === InspectionTemplateServiceTaskOptionTypes.FileAsType);
    if (fileAsType) {
      this.applyFileAsTypeSettings(fileAsType.settingsJson);
      this.fileAsTypeOptionId = fileAsType.id;
    }

    const responsibleParty = this.options.find(
      o => o.optionType === InspectionTemplateServiceTaskOptionTypes.ResponsibleParty
    );
    if (responsibleParty && responsibleParty.settingsJson) {
      try {
        const parsed = JSON.parse(responsibleParty.settingsJson);
        this.responsiblePartyForm.patchValue({
          responsibleParty: parsed.responsibleParty || null,
        });
      } catch {}
      this.responsiblePartyOptionId = responsibleParty.id;
    }

    const dueDate = this.options.find(o => o.optionType === InspectionTemplateServiceTaskOptionTypes.DueDate);
    if (dueDate && dueDate.settingsJson) {
      try {
        const parsed = JSON.parse(dueDate.settingsJson);
        this.dueDateForm.patchValue({
          dueDateDays: parsed.dueDateDays || null,
          dueDateDescription: parsed.dueDateDescription || null,
        });
      } catch {}
      this.dueDateOptionId = dueDate.id;
    }

    const assignee = this.options.find(o => o.optionType === InspectionTemplateServiceTaskOptionTypes.Assignee);
    if (assignee && assignee.settingsJson) {
      try {
        const parsed = JSON.parse(assignee.settingsJson);
        this.assigneeForm.patchValue({
          assignee: parsed.assignee || null,
        });
      } catch {}
      this.assigneeOptionId = assignee.id;
    }

    const sendToBoard = this.options.find(o => o.optionType === InspectionTemplateServiceTaskOptionTypes.SendToBoard);
    if (sendToBoard && sendToBoard.settingsJson) {
      try {
        const parsed = JSON.parse(sendToBoard.settingsJson);
        this.sendToBoardForm.patchValue({
          boardId: parsed.boardId || null,
          boardColumnId: parsed.boardColumnId || null,
        });
      } catch {}
      this.sendToBoardOptionId = sendToBoard.id;
    }
  }

  saveOption(form: FormGroup, optionType: InspectionTemplateServiceTaskOptionTypes): void {
    if (!form.valid) {
      return;
    }

    const formValue = form.value;
    let optionId: number | null = null;

    switch (optionType) {
      case InspectionTemplateServiceTaskOptionTypes.EstimatedCost:
        optionId = this.estimatedCostOptionId;
        break;
      case InspectionTemplateServiceTaskOptionTypes.FileAsType:
        optionId = this.fileAsTypeOptionId;
        break;
      case InspectionTemplateServiceTaskOptionTypes.ResponsibleParty:
        optionId = this.responsiblePartyOptionId;
        break;
      case InspectionTemplateServiceTaskOptionTypes.DueDate:
        optionId = this.dueDateOptionId;
        break;
      case InspectionTemplateServiceTaskOptionTypes.Assignee:
        optionId = this.assigneeOptionId;
        break;
      case InspectionTemplateServiceTaskOptionTypes.SendToBoard:
        optionId = this.sendToBoardOptionId;
        break;
    }

    const { optionType: formOptionTypeValue, ...rawSettingsData } = formValue;
    const settingsData = this.filterEmptySettings(rawSettingsData);
    const settingsJsonValue = this.buildSettingsJson(optionType, settingsData);

    const option: RestInspectionTemplateServiceTaskOptionsModelBase = {
      optionType: optionType,
      settingsJson: settingsJsonValue,
    };

    if (optionId) {
      this.store.dispatch(
        updateTemplateServiceTaskOption({
          templateId: this.data.templateId,
          optionId: optionId,
          option: option,
        })
      );
    } else {
      this.store.dispatch(
        createTemplateServiceTaskOption({
          templateId: this.data.templateId,
          option: option,
        })
      );
    }
  }

  private saveFileAsTypeSettings(): void {
    if (!this.fileAsTypeForm?.valid) {
      return;
    }

    const formValue = this.fileAsTypeForm.getRawValue() as Partial<FileAsTypeServiceTaskOptionsModel> & {
      fieldRequired?: boolean;
    };
    const { optionType: _optionType, ...rawSettings } = formValue as FileAsTypeServiceTaskOptionsModel & {
      optionType?: InspectionTemplateServiceTaskOptionTypes;
    };
    const availableSelection = this.normalizeNumberArray(
      (rawSettings.availableToUserDynamicTicketTypes as number[]) || [],
      false
    );
    const availableToUserDynamicTicketTypes =
      this.mergeDefaultSelection(availableSelection, rawSettings.defaultDynamicTicketType ?? null) ?? [];
    const userInput = !this.isUserCantChangeSubmitAs();

    const settingsData = this.filterEmptySettings({
      ...rawSettings,
      availableToUserDynamicTicketTypes,
      dynamicTicketTypeSettingsOverrides: this.buildFileAsTypeOverrides(),
      userInput,
    });

    const settingsJsonValue = this.buildSettingsJson(InspectionTemplateServiceTaskOptionTypes.FileAsType, settingsData);

    const option: RestInspectionTemplateServiceTaskOptionsModelBase = {
      optionType: InspectionTemplateServiceTaskOptionTypes.FileAsType,
      settingsJson: settingsJsonValue,
    };

    if (this.fileAsTypeOptionId) {
      this.store.dispatch(
        updateTemplateServiceTaskOption({
          templateId: this.data.templateId,
          optionId: this.fileAsTypeOptionId,
          option: option,
        })
      );
      return;
    }

    this.pendingAddOptionType = InspectionTemplateServiceTaskOptionTypes.FileAsType;
    this.pendingAddUserInput = this.selectedFieldDefinition === 'User input';
    this.store.dispatch(
      createTemplateServiceTaskOption({
        templateId: this.data.templateId,
        option: option,
      })
    );
  }

  onClose(result?: DialogResult): void {
    this.dialogRef.close(result);
  }

  deleteSetting(): void {
    if (this.selectedMobileFieldType !== null) {
      const optionId = this.getOptionIdByType(this.selectedMobileFieldType);
      if (optionId) {
        this.store.dispatch(
          deleteTemplateServiceTaskOption({
            templateId: this.data.templateId,
            optionId: optionId,
          })
        );
      }

      this.selectedFieldTypes = this.selectedFieldTypes.filter(type => type !== this.selectedMobileFieldType);
      this.selectedMobileFieldType = null;
      this.cdr.markForCheck();
      return;
    }

    if (this.selectedWorkflowFieldKey) {
      const optionId = this.getOptionIdByType(this.selectedWorkflowFieldKey);
      if (optionId) {
        this.store.dispatch(
          deleteTemplateServiceTaskOption({
            templateId: this.data.templateId,
            optionId: optionId,
          })
        );
      }
      this.workflowFieldTypes = this.workflowFieldTypes.filter(type => type !== this.selectedWorkflowFieldKey);
    }

    this.selectedWorkflowFieldKey = null;
    this.cdr.markForCheck();
  }

  toggleMobilePreview(): void {
    this.showMobilePreview = !this.showMobilePreview;
    this.cdr.markForCheck();
  }

  addWorkflowFieldOption(optionType: InspectionTemplateServiceTaskOptionTypes): void {
    if (!this.workflowOptionsEnabled) {
      return;
    }
    if (optionType === InspectionTemplateServiceTaskOptionTypes.FileAsType) {
      this.ensureFileAsTypeReferenceDataLoaded();
    }

    if (this.workflowFieldTypes.includes(optionType)) {
      this.selectedWorkflowFieldKey = optionType;
      this.selectedMobileFieldType = null;
      this.selectedFieldDefinition = 'Workflow';
      return;
    }

    const existingOption = this.getOptionByType(optionType);
    if (existingOption) {
      this.updateOptionUserInput(optionType, false);
    } else {
      this.pendingAddOptionType = optionType;
      this.pendingAddUserInput = false;
      this.store.dispatch(
        createTemplateServiceTaskOption({
          templateId: this.data.templateId,
          option: {
            optionType: optionType,
            settingsJson: this.buildSettingsJson(optionType, { userInput: false }),
          },
        })
      );
    }

    this.selectedWorkflowFieldKey = optionType;
    this.selectedMobileFieldType = null;
    this.selectedFieldDefinition = 'Workflow';
    this.cdr.markForCheck();
  }

  toggleSelectedFieldType(optionType: InspectionTemplateServiceTaskOptionTypes): void {
    if (this.selectedMobileFieldType === optionType) {
      return;
    }
    if (optionType === InspectionTemplateServiceTaskOptionTypes.FileAsType) {
      this.ensureFileAsTypeReferenceDataLoaded();
    }
    this.selectedMobileFieldType = optionType;
    this.selectedWorkflowFieldKey = null;
    if (this.selectedMobileFieldType !== null) {
      this.selectedFieldDefinition = 'User input';
    }
    this.cdr.markForCheck();
  }

  getSelectedFieldLabel(): string {
    if (this.selectedWorkflowFieldKey) {
      return this.fieldOptions.find(option => option.type === this.selectedWorkflowFieldKey)?.label + ' settings';
    }

    if (this.selectedMobileFieldType === null) return null;

    return this.fieldOptions.find(option => option.type === this.selectedMobileFieldType)?.label + ' settings';
  }

  addFieldOption(optionType: InspectionTemplateServiceTaskOptionTypes): void {
    if (this.selectedFieldTypes.includes(optionType)) {
      if (optionType === InspectionTemplateServiceTaskOptionTypes.FileAsType) {
        this.ensureFileAsTypeReferenceDataLoaded();
      }
      this.selectedMobileFieldType = optionType;
      this.selectedWorkflowFieldKey = null;
      this.selectedFieldDefinition = 'User input';
      return;
    }

    const existingOption = this.getOptionByType(optionType);
    if (existingOption) {
      this.updateOptionUserInput(optionType, true);
    } else {
      this.pendingAddOptionType = optionType;
      this.pendingAddUserInput = true;
      this.store.dispatch(
        createTemplateServiceTaskOption({
          templateId: this.data.templateId,
          option: {
            optionType: optionType,
            settingsJson: this.buildSettingsJson(optionType, { userInput: true }),
          },
        })
      );
    }

    this.selectedMobileFieldType = optionType;
    this.selectedWorkflowFieldKey = null;
    this.selectedFieldDefinition = 'User input';
    if (optionType === InspectionTemplateServiceTaskOptionTypes.FileAsType) {
      this.ensureFileAsTypeReferenceDataLoaded();
    }
    this.cdr.markForCheck();
  }

  private setSelectedFieldTypesFromOptions(): void {
    const userInputTypes = new Set<InspectionTemplateServiceTaskOptionTypes>();
    const workflowTypes = new Set<InspectionTemplateServiceTaskOptionTypes>();

    this.options.forEach(option => {
      let userInput = true;
      if (option.settingsJson) {
        try {
          const parsed = JSON.parse(option.settingsJson);
          if (typeof parsed.userInput === 'boolean') {
            userInput = parsed.userInput;
          }
        } catch {}
      }

      if (userInput) {
        userInputTypes.add(option.optionType);
      } else {
        workflowTypes.add(option.optionType);
      }
    });

    this.selectedFieldTypes = this.fieldOptions
      .filter(option => userInputTypes.has(option.type))
      .map(option => option.type);
    this.workflowFieldTypes = this.fieldOptions
      .filter(option => workflowTypes.has(option.type))
      .map(option => option.type);

    if (this.selectedMobileFieldType !== null && !userInputTypes.has(this.selectedMobileFieldType)) {
      this.selectedMobileFieldType = null;
    }
    if (this.selectedWorkflowFieldKey !== null && !workflowTypes.has(this.selectedWorkflowFieldKey)) {
      this.selectedWorkflowFieldKey = null;
    }

    if (this.selectedMobileFieldType !== null) {
      this.selectedFieldDefinition = 'User input';
    } else if (this.selectedWorkflowFieldKey !== null) {
      this.selectedFieldDefinition = 'Workflow';
    }
  }

  toggleWorkflowFieldType(optionKey: InspectionTemplateServiceTaskOptionTypes): void {
    if (!this.workflowOptionsEnabled) {
      return;
    }

    if (this.selectedWorkflowFieldKey === optionKey) {
      return;
    }
    if (optionKey === InspectionTemplateServiceTaskOptionTypes.FileAsType) {
      this.ensureFileAsTypeReferenceDataLoaded();
    }
    this.selectedWorkflowFieldKey = optionKey;
    this.selectedMobileFieldType = null;
    if (this.selectedWorkflowFieldKey !== null) {
      this.selectedFieldDefinition = 'Workflow';
    }
    this.cdr.markForCheck();
  }

  private getOptionIdByType(optionType: InspectionTemplateServiceTaskOptionTypes): number | null {
    switch (optionType) {
      case InspectionTemplateServiceTaskOptionTypes.EstimatedCost:
        return this.estimatedCostOptionId;
      case InspectionTemplateServiceTaskOptionTypes.FileAsType:
        return this.fileAsTypeOptionId;
      case InspectionTemplateServiceTaskOptionTypes.ResponsibleParty:
        return this.responsiblePartyOptionId;
      case InspectionTemplateServiceTaskOptionTypes.DueDate:
        return this.dueDateOptionId;
      case InspectionTemplateServiceTaskOptionTypes.Assignee:
        return this.assigneeOptionId;
      case InspectionTemplateServiceTaskOptionTypes.SendToBoard:
        return this.sendToBoardOptionId;
      default:
        return null;
    }
  }

  private getOptionByType(
    optionType: InspectionTemplateServiceTaskOptionTypes
  ): RestInspectionTemplateServiceTaskOptionsModel | undefined {
    return this.options.find(option => option.optionType === optionType);
  }

  private updateOptionUserInput(optionType: InspectionTemplateServiceTaskOptionTypes, userInput: boolean): void {
    const option = this.getOptionByType(optionType);
    const existingSettings = this.parseSettings(option?.settingsJson);
    const settingsJson = this.buildSettingsJson(optionType, {
      ...existingSettings,
      userInput,
    });

    if (option?.id) {
      this.store.dispatch(
        updateTemplateServiceTaskOption({
          templateId: this.data.templateId,
          optionId: option.id,
          option: {
            optionType,
            settingsJson,
          },
        })
      );
      return;
    }

    this.pendingAddOptionType = optionType;
    this.pendingAddUserInput = userInput;
    this.store.dispatch(
      createTemplateServiceTaskOption({
        templateId: this.data.templateId,
        option: {
          optionType,
          settingsJson,
        },
      })
    );
  }

  getSubmitAsLabel(value?: SubmitAsOptions | null): string {
    switch (value) {
      case SubmitAsOptions.WorkOrder:
        return 'Work Order';
      case SubmitAsOptions.AnyServiceTask:
        return 'Work Order or PropUp Ticket';
      case SubmitAsOptions.PropUpTicket:
      default:
        return 'PropUp Ticket';
    }
  }

  getSubmitAsAvailabilityLabel(value?: SubmitAsOptions | null): string {
    if (this.isUserCantChangeSubmitAs()) {
      return "User can't change";
    }
    if (value === SubmitAsOptions.AnyServiceTask) {
      return 'User can select Work Order or PropUp Ticket';
    }
    if (value === SubmitAsOptions.WorkOrder) {
      return 'User can select Work Order';
    }
    return 'User can select PropUp Ticket';
  }

  setSubmitAs(value: SubmitAsOptions): void {
    this.fileAsTypeForm.get('submitAs')?.setValue(value);
  }

  setSubmitAsAvailability(canChange: boolean): void {
    this.selectedFieldDefinition = canChange ? 'User input' : 'Workflow';
    if (canChange) {
      this.setSubmitAs(SubmitAsOptions.AnyServiceTask);
      this.fileAsTypeFastSave$.next();

      return;
    }
    const current = this.submitAsValue;
    if (current === SubmitAsOptions.AnyServiceTask) {
      const defaultId = this.fileAsTypeForm.get('defaultDynamicTicketType')?.value as number | null;
      const defaultType = defaultId ? this.dynamicTicketTypesMap.get(defaultId) : null;
      if (defaultType) {
        this.setSubmitAs(
          defaultType.builtInType === BuiltInTicketType.WorkOrder
            ? SubmitAsOptions.WorkOrder
            : SubmitAsOptions.PropUpTicket
        );
        this.fileAsTypeFastSave$.next();

        return;
      }
      this.setSubmitAs(SubmitAsOptions.WorkOrder);
    }
    this.fileAsTypeFastSave$.next();
  }

  isUserCantChangeSubmitAs(): boolean {
    return this.selectedFieldDefinition === 'Workflow';
  }

  isSubmitAsOptionSelected(option: SubmitAsOptions.WorkOrder | SubmitAsOptions.PropUpTicket): boolean {
    if (this.submitAsValue === SubmitAsOptions.AnyServiceTask) {
      return true;
    }
    return this.submitAsValue === option;
  }

  toggleSubmitAsOption(option: SubmitAsOptions.WorkOrder | SubmitAsOptions.PropUpTicket, checked: boolean): void {
    if (this.isUserCantChangeSubmitAs()) {
      this.selectedFieldDefinition = 'User input';
    }
    const selection = new Set<SubmitAsOptions>();
    if (this.submitAsValue === SubmitAsOptions.AnyServiceTask) {
      selection.add(SubmitAsOptions.WorkOrder);
      selection.add(SubmitAsOptions.PropUpTicket);
    } else {
      selection.add(this.submitAsValue);
    }

    if (checked) {
      selection.add(option);
    } else {
      selection.delete(option);
    }

    if (!selection.size) {
      selection.add(option);
    }

    if (selection.size === 2) {
      this.setSubmitAs(SubmitAsOptions.AnyServiceTask);
      return;
    }

    const [onlyOption] = Array.from(selection);
    this.setSubmitAs(onlyOption);
  }

  onOnlyOneWorkOrderChange(checked: boolean): void {
    this.fileAsTypeForm.get('canSubmitMoreThanOneTicket')?.setValue(!checked);
  }

  getDefaultDynamicTicketTypeLabel(category: 'workOrder' | 'ticket'): string {
    const defaultId = this.fileAsTypeForm.get('defaultDynamicTicketType')?.value as number | null;
    if (!defaultId) {
      return category === 'workOrder' ? 'Select work order type' : 'Select ticket type';
    }
    const type = this.dynamicTicketTypesMap.get(defaultId);
    if (!type) {
      return category === 'workOrder' ? 'Select work order type' : 'Select ticket type';
    }
    if (category === 'workOrder' && type.builtInType !== BuiltInTicketType.WorkOrder) {
      return 'Select work order type';
    }
    if (category === 'ticket' && type.builtInType === BuiltInTicketType.WorkOrder) {
      return 'Select ticket type';
    }
    return type.name || (category === 'workOrder' ? 'Select work order type' : 'Select ticket type');
  }

  setDefaultDynamicTicketType(typeId: number | null): void {
    this.fileAsTypeForm.get('defaultDynamicTicketType')?.setValue(typeId);
  }

  isAvailableDynamicTicketTypeSelected(category: 'workOrder' | 'ticket', typeId: number): boolean {
    const selected = new Set(this.getSelectedDynamicTicketTypeIds());
    if (!selected.has(typeId)) {
      return false;
    }
    const type = this.dynamicTicketTypesMap.get(typeId);
    if (!type) {
      return false;
    }
    if (category === 'workOrder') {
      return type.builtInType === BuiltInTicketType.WorkOrder;
    }
    return type.builtInType !== BuiltInTicketType.WorkOrder;
  }

  toggleAvailableDynamicTicketType(category: 'workOrder' | 'ticket', typeId: number): void {
    const type = this.dynamicTicketTypesMap.get(typeId);
    if (!type) {
      return;
    }
    if (category === 'workOrder' && type.builtInType !== BuiltInTicketType.WorkOrder) {
      return;
    }
    if (category === 'ticket' && type.builtInType === BuiltInTicketType.WorkOrder) {
      return;
    }
    const next = this.toggleArrayValue(this.getSelectedDynamicTicketTypeIds(), typeId);
    this.fileAsTypeForm.get('availableToUserDynamicTicketTypes')?.setValue(next);
  }

  isUserCantChangeDynamicTicketTypes(category: 'workOrder' | 'ticket'): boolean {
    return this.getAvailableDynamicTicketTypeIdsByCategory(category).length === 0;
  }

  onUserCantChangeDynamicTicketTypesChange(category: 'workOrder' | 'ticket', checked: boolean): void {
    if (checked) {
      this.setAvailableTypeSelection(category, []);
      return;
    }
    this.setAvailableTypeSelection(
      category,
      this.getDynamicTicketTypeOptions(category).map(option => option.value)
    );
  }

  getAvailableTypeTriggerLabel(category: 'workOrder' | 'ticket'): string {
    const selectedIds = this.getAvailableDynamicTicketTypeIdsByCategory(category);
    if (!selectedIds.length) {
      return "User can't change";
    }
    const options = this.getDynamicTicketTypeOptions(category);
    if (this.areAllOptionsSelected(selectedIds, options)) {
      return category === 'workOrder'
        ? 'User can select from all Work Order types'
        : 'User can select from all Ticket types';
    }
    const labels = this.buildOptionLabels(selectedIds, options);
    if (!labels.length) {
      return "User can't change";
    }
    if (labels.length === 1) {
      return `User can select from ${labels[0]}`;
    }
    return `User can select from ${labels.length} types`;
  }

  getDynamicTicketTypeOptions(category: 'workOrder' | 'ticket'): IRadioButtonOption<number>[] {
    return category === 'workOrder' ? this.workOrderTypeOptions : this.ticketTypeOptions;
  }

  getOverrideForm(key: string): FormGroup | undefined {
    return this.fileAsTypeOverrideForms.get(key);
  }

  onSetupPmsSettingsForType(dynamicTicketTypeId: number, event?: Event): void {
    event?.preventDefault();
    if (!this.hasPmsConnection()) {
      return;
    }
    if (this.pmsSetupLoading.has(dynamicTicketTypeId)) {
      return;
    }
    const credentialIds = Array.from(this.apiCredentialsMap.keys());
    if (!credentialIds.length) {
      return;
    }
    this.pmsSetupLoading.add(dynamicTicketTypeId);
    let pending = credentialIds.length;
    credentialIds.forEach(apiCredentialsId => {
      this.dynamicTicketTypeService
        .createDynamicTicketTypePmsSettings({ dynamicTicketTypeId, apiCredentialsId })
        .pipe(take(1))
        .subscribe({
          next: () => {
            pending -= 1;
            if (pending === 0) {
              this.pmsSetupLoading.delete(dynamicTicketTypeId);
              this.reloadDynamicTicketTypeSettings(dynamicTicketTypeId);
            }
          },
          error: () => {
            pending -= 1;
            if (pending === 0) {
              this.pmsSetupLoading.delete(dynamicTicketTypeId);
              this.reloadDynamicTicketTypeSettings(dynamicTicketTypeId);
            }
          },
        })
        .untilDestroyed(this);
    });
  }

  getOverrideDefaultOriginLabel(key: string): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    const value = form?.get('defaultOrigin')?.value as string | null | undefined;
    return value || 'no default origin';
  }

  getOverrideDefaultCategoryLabel(key: string): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return 'no default category';
    }
    const categoryValue = form.get('defaultCategoryId')?.value as string | null;
    const categoryLabel =
      !categoryValue || categoryValue === this.allCategoriesTreeValue
        ? 'no default category'
        : this.getCategoryLabel(categoryValue) || 'no default category';
    return categoryLabel;
  }

  hasOverrideDefaultOrigin(key: string): boolean {
    return this.getOverrideDefaultOriginLabel(key) !== 'no default origin';
  }

  hasOverrideDefaultCategory(key: string): boolean {
    return this.getOverrideDefaultCategoryLabel(key) !== 'no default category';
  }

  trackByOverrideKey(_: number, entry: FileAsTypeOverrideEntry): string {
    return entry.key;
  }

  getPmsProviderName(apiCredentialsId: number | null | undefined): string {
    if (!apiCredentialsId) {
      return 'PMS';
    }
    return this.apiCredentialsMap.get(apiCredentialsId)?.type || 'PMS';
  }

  getPmsDisplayName(): string {
    return this.pmsNames || 'PMS';
  }

  getDynamicTicketTypeName(typeId: number, appendWorkOrderLabel = false): string {
    const type = this.dynamicTicketTypesMap.get(typeId);
    if (!type?.name) {
      return 'Work Order';
    }
    if (!appendWorkOrderLabel) {
      return type.name;
    }
    return /work order/i.test(type.name) ? type.name : `${type.name} (Work Order)`;
  }

  getOverrideSelectionDisplay(key: string, controlName: keyof FileAsTypeOverrideFormValue): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return controlName === 'defaultOrigin' ? 'no default origin' : 'Any';
    }
    if (controlName === 'defaultOrigin') {
      const value = form.get('defaultOrigin')?.value;
      if (!value) {
        return 'no default origin';
      }
      return `default origin: ${value}`;
    }
    if (controlName === 'allowedOrigins') {
      const values = this.getArrayControlValue(form, 'allowedOrigins');
      if (!values.length) {
        return "User can't select non default origin";
      }
      const options = this.getAllowedOriginOptions(key);
      if (this.areAllOptionsSelected(values, options)) {
        return 'User can select any origin';
      }
      const labels = this.buildOptionLabels(values, options);
      const summary = labels.length ? labels.join(', ') : values.join(', ');
      return `User can select origin: ${summary}`;
    }
    const values = this.getArrayControlValue(form, controlName);
    if (!values.length) {
      return controlName === 'allowedCategories' ? 'None' : 'Any';
    }
    return this.formatSelectionLabel(controlName as string, values);
  }

  getOverrideCategorySelectionLabel(key: string): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return "User can't select non default category";
    }
    const values = this.getArrayControlValue(form, 'allowedCategories').filter(
      value => value !== this.allCategoriesTreeValue
    );
    if (!values.length) {
      return "User can't select non default category";
    }
    const options = this.getAllowedCategoryOptions(key);
    if (this.areAllOptionsSelected(values, options)) {
      return 'User can select any category';
    }
    const labels = values.map(value => this.getCategoryLabel(value) || value);
    const summary = labels.length ? this.formatLabelsWithLimit(labels, this.categorySummaryLimit) : values.join(', ');
    return `User can select category: ${summary}`;
  }

  getDefaultCategoryTrigger(key: string): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return 'default category is not set';
    }
    const value = form.get('defaultCategoryId')?.value as string | null;
    if (!value) {
      return 'default category is not set';
    }
    if (value === this.allCategoriesTreeValue) {
      return "User can't change default category";
    }
    const label = this.getCategoryLabel(value) || 'None';
    return `default category is ${label}`;
  }

  getDefaultTopicTrigger(key: string): string {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return 'default topic is not set';
    }
    const defaultCategoryId = form.get('defaultCategoryId')?.value as string | null;
    if (defaultCategoryId === this.allCategoriesTreeValue) {
      return "User can't change default topic";
    }
    const value = form.get('defaultTopicId')?.value as string | null;
    if (!value) {
      return "User can't change default topic";
    }
    const label = this.getTopicLabel(value) || 'None';
    return `default topic is ${label}`;
  }

  getAllowedOriginOptions(key: string): IRadioButtonOption<string>[] {
    const options = this.overrideOriginOptions.get(key) || [];
    const defaultOrigin = this.fileAsTypeOverrideForms.get(key)?.get('defaultOrigin')?.value;
    if (!defaultOrigin) {
      return options;
    }
    return options.filter(option => option.value !== defaultOrigin);
  }

  getAllowedCategoryOptions(key: string): IRadioButtonOption<string>[] {
    const defaultCategoryId = this.fileAsTypeOverrideForms.get(key)?.get('defaultCategoryId')?.value as string | null;
    if (!defaultCategoryId || defaultCategoryId === this.allCategoriesTreeValue) {
      return this.categorySelectOptions;
    }
    return this.categorySelectOptions.filter(option => option.value !== defaultCategoryId);
  }

  isUserCantChooseAllowedOrigins(key: string): boolean {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return true;
    }
    const values = this.getArrayControlValue(form, 'allowedOrigins');
    return values.length === 0;
  }

  isUserCantChooseAllowedCategories(key: string): boolean {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return true;
    }
    const values = this.getArrayControlValue(form, 'allowedCategories').filter(
      value => value !== this.allCategoriesTreeValue
    );
    return values.length === 0;
  }

  onUserCantChooseAllowedOriginsChange(key: string, checked: boolean): void {
    if (checked) {
      this.clearOverrideSelection(key, 'allowedOrigins');
      return;
    }
    this.selectAllAllowedOrigins(key);
  }

  onUserCantChooseAllowedCategoriesChange(key: string, checked: boolean): void {
    if (checked) {
      this.clearOverrideSelection(key, 'allowedCategories');
      this.fileAsTypeFastSave$.next();

      return;
    }
    this.selectAllAllowedCategories(key);
    this.fileAsTypeFastSave$.next();
  }

  toggleOverrideSelection(key: string, controlName: keyof FileAsTypeOverrideFormValue, value: string): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    const next = this.toggleArrayValue(this.getArrayControlValue(form, controlName), value);
    form.get(controlName)?.setValue(next);
  }

  isAllowedCategorySelected(key: string, value: string): boolean {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return false;
    }
    return this.getArrayControlValue(form, 'allowedCategories').includes(value);
  }

  toggleAllowedCategorySelection(key: string, value: string): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    const current = this.getArrayControlValue(form, 'allowedCategories').filter(
      item => item !== this.allCategoriesTreeValue
    );
    const next = this.toggleArrayValue(current, value);
    form.get('allowedCategories')?.setValue(next);
    this.clearOverrideSelectAllState(key);
    this.fileAsTypeFastSave$.next();
  }

  isAllAllowedCategoriesSelected(key: string): boolean {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return false;
    }
    const values = this.getArrayControlValue(form, 'allowedCategories').filter(
      value => value !== this.allCategoriesTreeValue
    );
    const options = this.getAllowedCategoryOptions(key);
    return this.areAllOptionsSelected(values, options);
  }

  onSelectAllAllowedCategoriesChange(key: string, checked: boolean): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    if (checked) {
      this.overrideSelectAllState.set(key, {
        categories: this.getArrayControlValue(form, 'allowedCategories').filter(
          value => value !== this.allCategoriesTreeValue
        ),
      });
      form.get('allowedCategories')?.setValue(this.getAllowedCategoryIds(key));
      this.fileAsTypeFastSave$.next();

      return;
    }
    const snapshot = this.overrideSelectAllState.get(key);
    this.overrideSelectAllState.delete(key);
    form.get('allowedCategories')?.setValue(snapshot ? [...snapshot.categories] : []);
    this.fileAsTypeFastSave$.next();
  }

  setOverrideControl(key: string, controlName: keyof FileAsTypeOverrideFormValue, value: string | null): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    form.get(controlName)?.setValue(value);
    if (controlName === 'defaultCategoryId') {
      if (!value || value === this.allCategoriesTreeValue) {
        form.get('defaultTopicId')?.setValue(null);
        return;
      }
      this.cleanDefaultTopic(form, value);
    }
  }

  getTopicOptionsForCategory(categoryId: string | null): IRadioButtonOption<string>[] {
    if (!categoryId || categoryId === this.allCategoriesTreeValue) {
      return [];
    }
    return this.topicsByCategory.get(Number(categoryId)) || [];
  }

  private handleSubmitAsChange(value: SubmitAsOptions): void {
    this.ensureFileAsTypeReferenceDataLoaded();
    if (value === SubmitAsOptions.WorkOrder) {
      this.setAvailableTypeSelection('ticket', []);
      const defaultId = this.fileAsTypeForm.get('defaultDynamicTicketType')?.value as number | null;
      if (defaultId && this.dynamicTicketTypesMap.get(defaultId)?.builtInType !== BuiltInTicketType.WorkOrder) {
        this.setDefaultDynamicTicketType(null);
      }
    }
    if (value === SubmitAsOptions.PropUpTicket) {
      this.setAvailableTypeSelection('workOrder', []);
      const defaultId = this.fileAsTypeForm.get('defaultDynamicTicketType')?.value as number | null;
      if (defaultId && this.dynamicTicketTypesMap.get(defaultId)?.builtInType === BuiltInTicketType.WorkOrder) {
        this.setDefaultDynamicTicketType(null);
      }
    }
  }

  private applyFileAsTypeSettings(settingsJson: string | null): void {
    const parsed = this.parseSettings(settingsJson) as Partial<FileAsTypeServiceTaskOptionsModel>;
    const normalized = this.normalizeFileAsTypeSettings(parsed);

    this.fileAsTypeForm.patchValue(
      {
        submitAs: normalized.submitAs,
        defaultDynamicTicketType: normalized.defaultDynamicTicketType,
        availableToUserDynamicTicketTypes: normalized.availableToUserDynamicTicketTypes,
        fieldRequired: normalized.isRequired ?? false,
        canSubmitMoreThanOneTicket: normalized.canSubmitMoreThanOneTicket,
        autoGeneratedTicketTitle: normalized.autoGeneratedTicketTitle,
        autoGeneratedTicketDescription: normalized.autoGeneratedTicketDescription,
        customTicketTitle: normalized.customTicketTitle,
        customTicketDescription: normalized.customTicketDescription,
        userCanEditTicketTitle: normalized.userCanEditTicketTitle,
        userCanEditTicketDescription: normalized.userCanEditTicketDescription,
      },
      { emitEvent: false }
    );

    this.fileAsTypeOverridesByKey.clear();
    (normalized.dynamicTicketTypeSettingsOverrides || []).forEach(override => {
      const key = this.buildOverrideKey(override.dynamicTicketTypeId, override.apiCredentialsId);
      this.fileAsTypeOverridesByKey.set(key, override);
    });

    this.syncFileAsTypeOverrides();
  }

  private setDynamicTicketTypes(types: DynamicTicketType[]): void {
    this.dynamicTicketTypes = [...types].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.dynamicTicketTypesMap.clear();
    this.dynamicTicketTypeSettingsByType.clear();
    this.dynamicTicketTypeSettingsLoading.clear();
    this.dynamicTicketTypes.forEach(type => this.dynamicTicketTypesMap.set(type.id, type));
    this.workOrderTypeOptions = this.dynamicTicketTypes
      .filter(type => type.builtInType === BuiltInTicketType.WorkOrder)
      .map(type => ({ value: type.id, label: type.name }));
    this.ticketTypeOptions = this.dynamicTicketTypes
      .filter(type => type.builtInType !== BuiltInTicketType.WorkOrder)
      .map(type => ({ value: type.id, label: type.name }));
  }

  private setCategoryOptions(categories: RestTicketCategoryModel[]): void {
    this.categories = categories.map(category => ({
      value: category.id,
      label: category.name,
      topics: (category.topics || []).map(topic => ({
        value: topic.id,
        label: topic.name,
      })),
    }));
    this.topics = this.categories.reduce<TopicOption[]>((acc, category) => acc.concat(category.topics), []);
    this.categorySelectOptions = this.categories.map(category => ({
      value: category.value.toString(),
      label: category.label,
    }));
    this.categoryLabelMap.clear();
    this.categoryLabelMap.set(this.allCategoriesTreeValue, "User can't choose");
    this.categorySelectOptions.forEach(option => this.categoryLabelMap.set(option.value, option.label));
    this.topicsByCategory.clear();
    this.topicLabelMap.clear();
    this.categories.forEach(category => {
      const topicOptions: IRadioButtonOption<string>[] = category.topics.map(topic => {
        this.topicLabelMap.set(topic.value.toString(), topic.label);
        return { value: topic.value.toString(), label: topic.label };
      });
      this.topicsByCategory.set(category.value, topicOptions);
    });
  }

  private captureCredentialOrigins(credentials: PmsCredentialModel[]): void {
    this.apiCredentialsMap.clear();
    credentials.forEach(cred => {
      const residentOrigins = (cred.pmsSyncConfiguration?.residentTicketOrigins || '')
        .split(',')
        .map(origin => origin.trim())
        .filter(origin => !!origin);
      this.apiCredentialsMap.set(cred.id, {
        type: cred.type,
        residentTicketOrigins: residentOrigins,
      });
    });
  }

  private setDynamicTicketTypeSettingsForType(
    dynamicTicketTypeId: number,
    settings: DynamicTicketTypePmsSettings[]
  ): void {
    this.dynamicTicketTypeSettingsByType.set(dynamicTicketTypeId, settings);
    this.clearOverrideOriginOptionsForType(dynamicTicketTypeId);
  }

  private clearOverrideOriginOptionsForType(dynamicTicketTypeId: number): void {
    Array.from(this.overrideOriginOptions.keys()).forEach(key => {
      if (this.parseOverrideKey(key).dynamicTicketTypeId === dynamicTicketTypeId) {
        this.overrideOriginOptions.delete(key);
      }
    });
  }

  private ensureDynamicTicketTypeSettingsLoaded(dynamicTicketTypeId: number): void {
    if (!this.hasPmsConnection()) {
      return;
    }
    if (this.dynamicTicketTypeSettingsByType.has(dynamicTicketTypeId)) {
      return;
    }
    if (this.dynamicTicketTypeSettingsLoading.has(dynamicTicketTypeId)) {
      return;
    }
    this.dynamicTicketTypeSettingsLoading.add(dynamicTicketTypeId);
    this.dynamicTicketTypeService
      .getDynamicTicketTypePmsSettingsList(dynamicTicketTypeId)
      .pipe(retry(1), take(1))
      .subscribe({
        next: settings => {
          this.dynamicTicketTypeSettingsLoading.delete(dynamicTicketTypeId);
          this.setDynamicTicketTypeSettingsForType(dynamicTicketTypeId, settings || []);
          this.syncFileAsTypeOverrides();
          this.cdr.markForCheck();
        },
        error: () => {
          this.dynamicTicketTypeSettingsLoading.delete(dynamicTicketTypeId);
          this.dynamicTicketTypeSettingsByType.set(dynamicTicketTypeId, []);
          this.cdr.markForCheck();
        },
      })
      .untilDestroyed(this);
  }

  private reloadDynamicTicketTypeSettings(dynamicTicketTypeId: number): void {
    this.dynamicTicketTypeSettingsByType.delete(dynamicTicketTypeId);
    this.dynamicTicketTypeSettingsLoading.delete(dynamicTicketTypeId);
    this.ensureDynamicTicketTypeSettingsLoaded(dynamicTicketTypeId);
  }

  private syncFileAsTypeOverrides(): void {
    const selectedWorkOrderIds = this.getSelectedDynamicTicketTypeIdsByCategory('workOrder');
    const nextKeys = new Set<string>();
    const placeholderEntries: FileAsTypeOverrideEntry[] = [];

    selectedWorkOrderIds.forEach(typeId => {
      this.ensureDynamicTicketTypeSettingsLoaded(typeId);
      const matchingSettings = this.dynamicTicketTypeSettingsByType.get(typeId);
      if (!matchingSettings) {
        return;
      }
      if (!matchingSettings.length) {
        let hasOverride = false;
        this.fileAsTypeOverridesByKey.forEach((override, key) => {
          if (override.dynamicTicketTypeId === typeId) {
            hasOverride = true;
            nextKeys.add(key);
            if (!this.overrideOriginOptions.has(key)) {
              this.overrideOriginOptions.set(key, makeRadioButtonOptions(override.availableToUserOrigins || []));
            }
            if (!this.fileAsTypeOverrideForms.has(key)) {
              const form = this.buildOverrideForm(this.getOverrideFormInitialValue(override));
              this.fileAsTypeOverrideForms.set(key, form);
              this.registerOverrideFormListener(key, form);
            }
          }
        });
        if (!hasOverride && this.hasPmsConnection()) {
          placeholderEntries.push({
            key: this.buildMissingOverrideKey(typeId),
            dynamicTicketTypeId: typeId,
            apiCredentialsId: null,
            isPlaceholder: true,
          });
        }
        return;
      }

      matchingSettings.forEach(setting => {
        const key = this.buildOverrideKey(typeId, setting.apiCredentialsId);
        nextKeys.add(key);
        if (!this.overrideOriginOptions.has(key)) {
          this.overrideOriginOptions.set(key, this.buildOriginOptions(setting));
        }
        if (this.fileAsTypeOverrideForms.has(key)) {
          return;
        }
        const existingOverride = this.fileAsTypeOverridesByKey.get(key);
        const initialValue = existingOverride
          ? this.getOverrideFormInitialValue(existingOverride)
          : this.getOverrideFormInitialValue(this.buildOverrideFromSetting(setting));
        const form = this.buildOverrideForm(initialValue);
        this.fileAsTypeOverrideForms.set(key, form);
        this.registerOverrideFormListener(key, form);
      });
    });

    Array.from(this.fileAsTypeOverrideForms.keys()).forEach(key => {
      if (!nextKeys.has(key)) {
        this.fileAsTypeOverrideForms.delete(key);
        this.overrideOriginOptions.delete(key);
        this.overrideSelectAllState.delete(key);
      }
    });

    const entries = Array.from(nextKeys).map(key => {
      const parsed = this.parseOverrideKey(key);
      return {
        key,
        dynamicTicketTypeId: parsed.dynamicTicketTypeId,
        apiCredentialsId: parsed.apiCredentialsId,
      };
    });

    this.fileAsTypeOverrideEntries = [...entries, ...placeholderEntries].sort((a, b) => {
      const nameA = this.getDynamicTicketTypeName(a.dynamicTicketTypeId, true);
      const nameB = this.getDynamicTicketTypeName(b.dynamicTicketTypeId, true);
      if (nameA !== nameB) {
        return nameA.localeCompare(nameB);
      }
      const pmsA = a.apiCredentialsId ? this.getPmsProviderName(a.apiCredentialsId) : 'PMS';
      const pmsB = b.apiCredentialsId ? this.getPmsProviderName(b.apiCredentialsId) : 'PMS';
      return pmsA.localeCompare(pmsB);
    });

    this.cdr.markForCheck();
  }

  private buildOverrideFromSetting(setting: DynamicTicketTypePmsSettings): DynamicTicketTypeSettingsOverride {
    return {
      dynamicTicketTypeId: setting.dynamicTicketTypeId,
      apiCredentialsId: setting.apiCredentialsId,
      defaultOrigin: setting.defaultOrigin || null,
      defaultCategoryId: setting.defaultCategoryId ?? null,
      defaultTopicId: setting.defaultTopicId ?? null,
      availableToUserOrigins: setting.availableOriginsToSelectByUser ? [...setting.availableOriginsToSelectByUser] : [],
      availableToUserCategories:
        setting.availableCategoriesToSelectByUser != null ? [...setting.availableCategoriesToSelectByUser] : null,
      availableToUserTopics:
        setting.availableTopicsToSelectByUser != null ? [...setting.availableTopicsToSelectByUser] : null,
    };
  }

  private buildOverrideForm(initialValue: FileAsTypeOverrideFormValue): FormGroup {
    const form = this.fb.group({
      defaultOrigin: [initialValue.defaultOrigin],
      allowedOrigins: [initialValue.allowedOrigins ? [...initialValue.allowedOrigins] : []],
      defaultCategoryId: [initialValue.defaultCategoryId],
      defaultTopicId: [initialValue.defaultTopicId],
      allowedCategories: [initialValue.allowedCategories ? [...initialValue.allowedCategories] : []],
    });
    this.cleanDefaultTopic(form, form.get('defaultCategoryId')?.value);
    return form;
  }

  private registerOverrideFormListener(key: string, form: FormGroup): void {
    form
      .get('defaultOrigin')
      ?.valueChanges.pipe(skip(1))
      .subscribe(value => {
        this.removeSelectionValue(form, 'allowedOrigins', value);
      })
      .untilDestroyed(this);

    form
      .get('defaultCategoryId')
      ?.valueChanges.pipe(skip(1))
      .subscribe(() => {
        const categoryId = form.get('defaultCategoryId')?.value;
        this.cleanDefaultTopic(form, categoryId);
      })
      .untilDestroyed(this);

    form.valueChanges
      .pipe(skip(1))
      .subscribe(() => {
        this.fileAsTypeFastSave$.next();
      })
      .untilDestroyed(this);
  }

  private getOverrideFormInitialValue(
    override: DynamicTicketTypeSettingsOverride | null | undefined
  ): FileAsTypeOverrideFormValue {
    if (!override) {
      return {
        defaultOrigin: null,
        allowedOrigins: [],
        defaultCategoryId: this.allCategoriesTreeValue,
        defaultTopicId: null,
        allowedCategories: [],
      };
    }
    const categories = this.buildUiCategorySelectionFromBackend(override.availableToUserCategories);
    const defaultCategoryId =
      override.defaultCategoryId != null ? override.defaultCategoryId.toString() : this.allCategoriesTreeValue;
    return {
      defaultOrigin: override.defaultOrigin || null,
      allowedOrigins: override.availableToUserOrigins ? [...override.availableToUserOrigins] : [],
      defaultCategoryId,
      defaultTopicId: override.defaultTopicId != null ? override.defaultTopicId.toString() : null,
      allowedCategories: categories,
    };
  }

  private buildFileAsTypeOverrides(): DynamicTicketTypeSettingsOverride[] {
    return this.fileAsTypeOverrideEntries
      .map(entry => {
        const form = this.fileAsTypeOverrideForms.get(entry.key);
        if (!form) {
          return null;
        }
        const payload = this.buildOverridePayload(entry, form.getRawValue() as FileAsTypeOverrideFormValue);
        return payload;
      })
      .filter((override): override is DynamicTicketTypeSettingsOverride => !!override);
  }

  private buildOverridePayload(
    entry: FileAsTypeOverrideEntry,
    formValue: FileAsTypeOverrideFormValue
  ): DynamicTicketTypeSettingsOverride {
    const allowedOriginsSelection = this.normalizeStringArray(formValue.allowedOrigins);
    const allowedOrigins = this.mergeDefaultSelection(allowedOriginsSelection, formValue.defaultOrigin);
    const defaultTopicId = this.parseNumber(formValue.defaultTopicId);
    const allowedSelection = this.normalizeCategorySelection(formValue.allowedCategories, true);

    return {
      dynamicTicketTypeId: entry.dynamicTicketTypeId,
      apiCredentialsId: entry.apiCredentialsId,
      defaultOrigin: formValue.defaultOrigin,
      defaultCategoryId: this.parseCategoryValue(formValue.defaultCategoryId),
      defaultTopicId: defaultTopicId,
      availableToUserOrigins: allowedOrigins ?? [],
      availableToUserCategories: allowedSelection.hasRootSelection ? null : allowedSelection.categories,
      availableToUserTopics: allowedSelection.hasRootSelection ? null : [],
    };
  }

  private buildOriginOptions(setting: DynamicTicketTypePmsSettings): IRadioButtonOption<string>[] {
    const originsSet = new Set<string>();
    (setting.availableOriginsToSelectByUser || []).forEach(origin => {
      if (origin) {
        originsSet.add(origin);
      }
    });
    const credentialOrigins = this.apiCredentialsMap.get(setting.apiCredentialsId)?.residentTicketOrigins || [];
    credentialOrigins.forEach(origin => originsSet.add(origin));
    return makeRadioButtonOptions(Array.from(originsSet));
  }

  private getSelectedDynamicTicketTypeIdsByCategory(category: 'workOrder' | 'ticket'): number[] {
    const selected = new Set(this.getSelectedDynamicTicketTypeIds());
    const defaultId = this.fileAsTypeForm.get('defaultDynamicTicketType')?.value as number | null;
    if (defaultId) {
      selected.add(defaultId);
    }
    return Array.from(selected).filter(id => {
      const type = this.dynamicTicketTypesMap.get(id);
      if (!type) {
        return false;
      }
      return category === 'workOrder'
        ? type.builtInType === BuiltInTicketType.WorkOrder
        : type.builtInType !== BuiltInTicketType.WorkOrder;
    });
  }

  private getAvailableDynamicTicketTypeIdsByCategory(category: 'workOrder' | 'ticket'): number[] {
    return this.getSelectedDynamicTicketTypeIds().filter(id => {
      const type = this.dynamicTicketTypesMap.get(id);
      if (!type) {
        return false;
      }
      return category === 'workOrder'
        ? type.builtInType === BuiltInTicketType.WorkOrder
        : type.builtInType !== BuiltInTicketType.WorkOrder;
    });
  }

  private getSelectedDynamicTicketTypeIds(): number[] {
    const value = this.fileAsTypeForm.get('availableToUserDynamicTicketTypes')?.value;
    return Array.isArray(value) ? [...value] : [];
  }

  private setAvailableTypeSelection(category: 'workOrder' | 'ticket', values: number[]): void {
    const current = new Set(this.getSelectedDynamicTicketTypeIds());
    const categoryIds = new Set(this.getDynamicTicketTypeOptions(category).map(option => option.value));
    categoryIds.forEach(id => current.delete(id));
    values.forEach(id => current.add(id));
    this.fileAsTypeForm.get('availableToUserDynamicTicketTypes')?.setValue(Array.from(current));
  }

  private selectAllAllowedOrigins(key: string): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    const options = this.getAllowedOriginOptions(key);
    form.get('allowedOrigins')?.setValue(options.map(option => option.value));
  }

  private selectAllAllowedCategories(key: string): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    if (!form) {
      return;
    }
    form.get('allowedCategories')?.setValue(this.getAllowedCategoryIds(key));
  }

  private clearOverrideSelection(key: string, controlName: keyof FileAsTypeOverrideFormValue): void {
    const form = this.fileAsTypeOverrideForms.get(key);
    form?.get(controlName)?.setValue([]);
    if (controlName === 'allowedCategories') {
      this.clearOverrideSelectAllState(key);
    }
  }

  private clearOverrideSelectAllState(key: string): void {
    this.overrideSelectAllState.delete(key);
  }

  private getAllowedCategoryIds(key: string): string[] {
    return this.getAllowedCategoryOptions(key).map(option => option.value);
  }

  private buildOverrideKey(dynamicTicketTypeId: number, apiCredentialsId: number): string {
    return `${dynamicTicketTypeId}:${apiCredentialsId}`;
  }

  private buildMissingOverrideKey(dynamicTicketTypeId: number): string {
    return `missing:${dynamicTicketTypeId}`;
  }

  private parseOverrideKey(key: string): { dynamicTicketTypeId: number; apiCredentialsId: number } {
    const [dynamicTicketTypeId, apiCredentialsId] = key.split(':').map(value => Number(value));
    return { dynamicTicketTypeId, apiCredentialsId };
  }

  private normalizeFileAsTypeSettings(
    parsed: Partial<FileAsTypeServiceTaskOptionsModel>
  ): FileAsTypeServiceTaskOptionsModel {
    return {
      submitAs: parsed.submitAs ?? SubmitAsOptions.PropUpTicket,
      defaultDynamicTicketType: this.parseNumber(parsed.defaultDynamicTicketType as number | string | null),
      availableToUserDynamicTicketTypes:
        this.normalizeNumberArray(parsed.availableToUserDynamicTicketTypes, false) ?? [],
      dynamicTicketTypeSettingsOverrides: (parsed.dynamicTicketTypeSettingsOverrides || [])
        .map(override => this.normalizeDynamicTicketTypeOverride(override))
        .filter((override): override is DynamicTicketTypeSettingsOverride => !!override),
      canSubmitMoreThanOneTicket: parsed.canSubmitMoreThanOneTicket ?? false,
      autoGeneratedTicketTitle: parsed.autoGeneratedTicketTitle ?? true,
      autoGeneratedTicketDescription: parsed.autoGeneratedTicketDescription ?? true,
      customTicketTitle: parsed.customTicketTitle ?? '',
      customTicketDescription: parsed.customTicketDescription ?? '',
      userCanEditTicketTitle: parsed.userCanEditTicketTitle ?? true,
      userCanEditTicketDescription: parsed.userCanEditTicketDescription ?? true,
      isRequired: parsed.isRequired ?? (parsed as { fieldRequired?: boolean }).fieldRequired ?? false,
    };
  }

  private normalizeDynamicTicketTypeOverride(
    override: Partial<DynamicTicketTypeSettingsOverride> | null | undefined
  ): DynamicTicketTypeSettingsOverride | null {
    if (!override) {
      return null;
    }
    const dynamicTicketTypeId = Number(override.dynamicTicketTypeId);
    const apiCredentialsId = Number(override.apiCredentialsId);
    if (Number.isNaN(dynamicTicketTypeId) || Number.isNaN(apiCredentialsId)) {
      return null;
    }
    const normalizedCategories =
      override.availableToUserCategories == null
        ? null
        : this.normalizeNumberArray(override.availableToUserCategories as number[], false) ?? [];
    const normalizedTopics =
      override.availableToUserTopics == null
        ? null
        : this.normalizeNumberArray(override.availableToUserTopics as number[], false) ?? [];

    return {
      dynamicTicketTypeId,
      apiCredentialsId,
      defaultOrigin: override.defaultOrigin ?? null,
      defaultCategoryId: this.parseNumber(override.defaultCategoryId as number | string | null),
      defaultTopicId: this.parseNumber(override.defaultTopicId as number | string | null),
      availableToUserOrigins: Array.isArray(override.availableToUserOrigins)
        ? override.availableToUserOrigins.filter(origin => !!origin)
        : [],
      availableToUserCategories: normalizedCategories && normalizedCategories.length ? normalizedCategories : null,
      availableToUserTopics: normalizedTopics && normalizedTopics.length ? normalizedTopics : null,
    };
  }

  private buildUiCategorySelectionFromBackend(categories?: number[] | null): string[] {
    if (categories == null) {
      return [];
    }
    return categories.map(value => value.toString());
  }

  private normalizeCategorySelection(
    selectedCategories?: string[],
    allowRootSelection = false
  ): { hasRootSelection: boolean; categories: number[] | null } {
    const categoryValues = (selectedCategories || [])
      .filter(value => value !== undefined && value !== null)
      .map(value => value.toString());

    let hasRootSelection = false;
    const filteredCategories = categoryValues.filter(value => value !== this.allCategoriesTreeValue);
    if (allowRootSelection) {
      hasRootSelection = categoryValues.includes(this.allCategoriesTreeValue) || filteredCategories.length === 0;
    }

    const normalizedCategories = this.normalizeNumberArray(filteredCategories, false);
    if (hasRootSelection || !normalizedCategories?.length) {
      return { hasRootSelection: true, categories: null };
    }

    return {
      hasRootSelection: false,
      categories: normalizedCategories,
    };
  }

  private normalizeStringArray(values?: string[]): string[] | null {
    if (!values || !values.length) {
      return null;
    }
    const normalized = values.map(value => value?.toString().trim()).filter(value => !!value);
    return normalized.length ? normalized : null;
  }

  private normalizeNumberArray(values?: (string | number)[], emptyAsNull = true): number[] | null {
    if (!values || !values.length) {
      return emptyAsNull ? null : [];
    }
    const normalized = values.map(value => Number(value)).filter(value => !Number.isNaN(value));
    if (!normalized.length) {
      return emptyAsNull ? null : [];
    }
    return normalized;
  }

  private mergeDefaultSelection<T>(values: T[] | null, defaultValue: T | null): T[] | null {
    if (defaultValue === null || defaultValue === undefined) {
      return values;
    }
    const list = values ? [...values] : [];
    if (!list.includes(defaultValue)) {
      list.push(defaultValue);
    }
    return list.length <= 1 ? null : list;
  }

  private parseNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private parseCategoryValue(value: string | null): number | null {
    if (value === this.allCategoriesTreeValue) {
      return null;
    }
    return this.parseNumber(value);
  }

  private getArrayControlValue(
    form: FormGroup | undefined,
    controlName: string | keyof FileAsTypeOverrideFormValue
  ): string[] {
    if (!form) {
      return [];
    }
    const value = form.get(controlName as string)?.value;
    return Array.isArray(value) ? [...value] : [];
  }

  private toggleArrayValue<T>(values: T[], value: T): T[] {
    const next = [...values];
    const index = next.indexOf(value);
    if (index >= 0) {
      next.splice(index, 1);
    } else {
      next.push(value);
    }
    return next;
  }

  private removeSelectionValue(
    form: FormGroup,
    controlName: keyof FileAsTypeOverrideFormValue,
    value: string | null
  ): void {
    if (!value) {
      return;
    }
    const control = form.get(controlName as string);
    if (!control) {
      return;
    }
    const current = this.getArrayControlValue(form, controlName);
    if (!current.length) {
      return;
    }
    const filtered = current.filter(item => item !== value);
    if (filtered.length !== current.length) {
      control.setValue(filtered, { emitEvent: false });
    }
  }

  private areAllOptionsSelected<T>(values: T[], options: IRadioButtonOption<T>[]): boolean {
    if (!options.length) {
      return false;
    }
    const selection = new Set(values);
    return options.every(option => selection.has(option.value));
  }

  private buildOptionLabels<T>(values: T[], options: IRadioButtonOption<T>[]): string[] {
    const labels: string[] = [];
    const selection = new Set(values);
    options.forEach(option => {
      if (selection.has(option.value)) {
        labels.push(option.label || `${option.value}`);
        selection.delete(option.value);
      }
    });
    selection.forEach(value => labels.push(`${value}`));
    return labels;
  }

  private formatLabelsWithLimit(labels: string[], limit: number): string {
    if (labels.length <= limit) {
      return labels.join(', ');
    }
    return `${labels.slice(0, limit).join(', ')} +${labels.length - limit} more`;
  }

  private formatSelectionLabel(controlName: string, values: string[]): string {
    const labels = values.map(value => this.getSelectionLabel(controlName, value)).filter(Boolean);
    if (!labels.length) {
      return controlName === 'allowedOrigins' ? 'Any' : 'None';
    }
    if (labels.length === 1) {
      return labels[0];
    }
    return `${labels.length} selected`;
  }

  private getSelectionLabel(controlName: string, value: string): string {
    switch (controlName) {
      case 'allowedOrigins':
        return value;
      case 'allowedCategories':
        return this.getCategoryLabel(value) || value;
      default:
        return value;
    }
  }

  private getCategoryLabel(value: string | null): string {
    if (!value) {
      return '';
    }
    return this.categoryLabelMap.get(value) || '';
  }

  private getTopicLabel(value: string | null): string {
    if (!value) {
      return '';
    }
    return this.topicLabelMap.get(value) || '';
  }

  private cleanDefaultTopic(form: FormGroup, categoryId: string | null): void {
    const control = form.get('defaultTopicId');
    if (!control) {
      return;
    }
    if (!this.topicsByCategory.size) {
      return;
    }
    if (!categoryId || categoryId === this.allCategoriesTreeValue) {
      control.setValue(null, { emitEvent: false });
      return;
    }
    const allowedTopicIds = this.getTopicOptionsForCategory(categoryId).map(option => option.value);
    if (control.value && !allowedTopicIds.includes(control.value)) {
      control.setValue(null, { emitEvent: false });
    }
  }

  private parseSettings(settingsJson: string | null | undefined): Record<string, unknown> {
    if (!settingsJson) {
      return {};
    }

    try {
      const parsed = JSON.parse(settingsJson);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private filterEmptySettings(settingsData: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(settingsData).filter(([, value]) => value !== null && value !== '' && value !== undefined)
    );
  }

  private buildSettingsJson(
    optionType: InspectionTemplateServiceTaskOptionTypes,
    settingsData: Record<string, unknown> = {}
  ): string {
    return JSON.stringify(this.buildSettings(optionType, settingsData));
  }

  private buildSettings(
    optionType: InspectionTemplateServiceTaskOptionTypes,
    settingsData: Record<string, unknown>
  ): Record<string, unknown> {
    const userInput =
      typeof settingsData.userInput === 'boolean'
        ? (settingsData.userInput as boolean)
        : this.selectedFieldDefinition === 'User input';
    const isRequired =
      typeof settingsData.isRequired === 'boolean'
        ? (settingsData.isRequired as boolean)
        : typeof settingsData.fieldRequired === 'boolean'
        ? (settingsData.fieldRequired as boolean)
        : false;

    const baseSettings = { userInput, isRequired };

    switch (optionType) {
      case InspectionTemplateServiceTaskOptionTypes.EstimatedCost:
        return {
          useTimeAndMaterials: false,
          defaultValue: 0,
          defaultTime: 0,
          defaultLaborCost: 0,
          defaultMaterials: 0,
          inputMethods: InspectionTemplateServiceTaskEstimatedCostInputMethods.Manually,
          partsCatalogGroupIds: [],
          ...settingsData,
          ...baseSettings,
        };
      case InspectionTemplateServiceTaskOptionTypes.FileAsType:
        return {
          submitAs: SubmitAsOptions.PropUpTicket,
          defaultDynamicTicketType: null,
          availableToUserDynamicTicketTypes: [],
          dynamicTicketTypeSettingsOverrides: [],
          canSubmitMoreThanOneTicket: false,
          autoGeneratedTicketTitle: true,
          autoGeneratedTicketDescription: true,
          customTicketTitle: '',
          customTicketDescription: '',
          userCanEditTicketTitle: true,
          userCanEditTicketDescription: true,
          ...settingsData,
          ...baseSettings,
        };
      case InspectionTemplateServiceTaskOptionTypes.ResponsibleParty:
      case InspectionTemplateServiceTaskOptionTypes.DueDate:
      case InspectionTemplateServiceTaskOptionTypes.Assignee:
      case InspectionTemplateServiceTaskOptionTypes.SendToBoard:
        return {
          ...settingsData,
          ...baseSettings,
        };
      default:
        return {
          ...settingsData,
          ...baseSettings,
        };
    }
  }
}
